import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AuthContextType, UserProfile } from './auth/types';
import { useUserData } from './auth/useUserData';
import { signInUser, signUpUser, signOutUser } from './auth/authService';
import { updateUserProfile } from './auth/userDataService';
import { useTranslation } from 'react-i18next';
import { cleanupLegacyLocalStorage } from '@/utils/localStorageCleanup';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const { t } = useTranslation();

  const {
    profile,
    currentOrganization,
    userMemberships,
    currentUserRole,
    fetchUserData,
    switchToOrganization,
    clearUserData,
  } = useUserData();

  useEffect(() => {
    console.log('AuthContext: Setting up auth state listener');
    console.log('AuthContext: Current user role:', currentUserRole);

    // Clean up legacy localStorage data on app initialization
    cleanupLegacyLocalStorage();

    // Get initial session first
    const getInitialSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      console.log('AuthContext: Initial session:', session?.user?.id, error);

      if (session) {
        setSession(session);
        setUser(session.user);
        // Use setTimeout to avoid blocking
        setTimeout(() => {
          fetchUserData(session.user.id);
        }, 0);
      } else {
        setLoading(false);
      }
    };

    getInitialSession();

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('AuthContext: Auth state changed:', event, session?.user?.id);

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user && event !== 'INITIAL_SESSION') {
        // Use setTimeout to avoid blocking the auth state change
        setTimeout(() => {
          fetchUserData(session.user.id);
        }, 0);
      } else if (!session) {
        clearUserData();
      }

      setLoading(false);
    });

    return () => {
      console.log('AuthContext: Cleaning up auth state listener');
      subscription.unsubscribe();
    };
  }, [fetchUserData, clearUserData, currentUserRole]);

  const signIn = async (email: string, password: string) => {
    const { error } = await signInUser(email, password);

    if (error) {
      toast.error(error.message);
    }

    return { error };
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    organizationId: string
  ) => {
    const { error } = await signUpUser(
      email,
      password,
      fullName,
      organizationId
    );

    if (error) {
      toast(error.message);
    } else {
      toast.success(t('auth.signUpSuccess'));
    }

    return { error };
  };

  const signOut = async () => {
    const { error } = await signOutUser();
    if (error) {
      toast(error.message);
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: new Error('No user logged in') };

    const { error } = await updateUserProfile(user.id, updates);

    if (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update profile';
      toast(error.message);
      return {
        error: error instanceof Error ? error : new Error(errorMessage),
      };
    }

    // Refresh profile data
    await fetchUserData(user.id);

    toast.success(t('auth.profileUpdateSuccess'));

    return { error: null };
  };

  const switchOrganization = async (organizationId: string) => {
    await switchToOrganization(organizationId);
  };

  const value = {
    user,
    session,
    profile,
    currentOrganization,
    userMemberships,
    currentUserRole,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    switchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Re-export types for backward compatibility
export type { UserProfile, Organization } from './auth/types';
